import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import { useStartEnhancedBlandCallWorkflowMutation } from '@/services/api/blandService';
import type {
  TreatmentRecordRequestChannelType,
  VoiceAiCallArgs,
} from '@/services/api/generated/treatmentRecordRequestsService';
import { treatmentRecordRequestService } from '@/services/api/treatmentRecordRequestService';
import { Task } from '@/services/types/client-intake-types';

/** Details fetched for treatment record requests */
interface TreatmentRecordRequestDetails {
  id: string;
  provider: string;
  provider_id: string;
  status: string;
  channel?: {
    type?: TreatmentRecordRequestChannelType;
    phone_number?: string;
    email?: string;
    fax?: string;
  };
  metadata?: {
    ad_hoc_voice_ai_args?: VoiceAiCallArgs;
    request_submitted_at?: string;
  };
}

export const useTaskVoiceAIBulkAction = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [startBlandCallWorkflow] = useStartEnhancedBlandCallWorkflowMutation();
  const [sendTreatmentBlandCall] =
    treatmentRecordRequestService.useMattersProvidersRecordRequestsSendBlandCallCreateMutation();

  const processEnhancedBlandTasks = useCallback(
    async (tasks: Task[]) => {
      if (tasks.length === 0) return { success: 0, failed: 0 };

      const results = await Promise.allSettled(
        tasks.map((task) =>
          startBlandCallWorkflow({
            matterId: task.matter,
            taskId: task.id,
          }).unwrap(),
        ),
      );

      return {
        success: results.filter((r) => r.status === 'fulfilled').length,
        failed: results.filter((r) => r.status === 'rejected').length,
      };
    },
    [startBlandCallWorkflow],
  );

  const processTreatmentTasks = useCallback(
    async (tasks: Task[]) => {
      if (tasks.length === 0) return { success: 0, failed: 0, skipped: 0 };

      // Fetch full details for each treatment record request
      const requestDetails = await Promise.allSettled(
        tasks.map(async (task) => {
          const request = task.treatment_record_request;
          if (!request) throw new Error('No treatment record request');

          const response = await fetch(
            `${import.meta.env.VITE_APP_DJANGO_BACKEND_HOST}/api/matters/${task.matter}/providers/${request.provider}/record-requests/${request.id}/`,
            { credentials: 'include' },
          );

          if (!response.ok) {
            throw new Error('Failed to fetch treatment record request');
          }

          const result: TreatmentRecordRequestDetails = await response.json();
          return { task, request: result };
        }),
      );

      // Filter for requests with 'request_submitted' status
      const eligibleRequests = requestDetails
        .filter(
          (
            result,
          ): result is PromiseFulfilledResult<{
            task: Task;
            request: TreatmentRecordRequestDetails;
          }> =>
            result.status === 'fulfilled' &&
            result.value.request.status === 'request_submitted',
        )
        .map((result) => result.value);

      const skipped = tasks.length - eligibleRequests.length;

      if (eligibleRequests.length === 0) {
        return { success: 0, failed: 0, skipped };
      }

      const callResults = await Promise.allSettled(
        eligibleRequests.map(async ({ task, request }) => {
          const requestMethod: TreatmentRecordRequestChannelType =
            request.channel?.type ?? 'human';

          const defaultVoiceAiArgs: VoiceAiCallArgs = {
            patient_information: {
              client_name: '',
              client_date_of_birth: '',
            },
            request_details: {
              request_method: requestMethod,
              request_sent_at:
                request.metadata?.request_submitted_at ??
                new Date().toISOString(),
              request_method_target:
                request.channel?.phone_number ??
                request.channel?.email ??
                request.channel?.fax ??
                '',
              contact_number: request.channel?.phone_number ?? '',
            },
            requestee_information: {
              requestee_name: task.firm_name ?? '',
            },
          };

          const voiceAiArgs =
            request.metadata?.ad_hoc_voice_ai_args ?? defaultVoiceAiArgs;

          await sendTreatmentBlandCall({
            matterId: task.matter,
            providerId: request.provider_id,
            requestId: request.id,
            voiceAiCallArgs: voiceAiArgs,
          }).unwrap();
        }),
      );

      return {
        success: callResults.filter((r) => r.status === 'fulfilled').length,
        failed: callResults.filter((r) => r.status === 'rejected').length,
        skipped,
      };
    },
    [sendTreatmentBlandCall],
  );

  const triggerVoiceAI = useCallback(
    async (selectedTasks: Task[]) => {
      // Categorize tasks by eligibility type
      const enhancedBlandTasks = selectedTasks.filter((task) => {
        const hasPoliceReport = task.police_report_id || task.police_report;
        const hasCoverage = task.coverage_id || task.coverage;
        return hasPoliceReport || hasCoverage;
      });

      const treatmentTasks = selectedTasks.filter(
        (task) =>
          task.treatment_record_request &&
          // Don't double-count tasks that qualify for enhanced bland
          !enhancedBlandTasks.includes(task),
      );

      const eligibleCount = enhancedBlandTasks.length + treatmentTasks.length;
      const initialSkippedCount = selectedTasks.length - eligibleCount;

      if (eligibleCount === 0) {
        toast.error(
          'No selected tasks are eligible for Voice AI calls. Tasks need either a police report, coverage, or treatment record request.',
        );
        return;
      }

      setIsLoading(true);

      try {
        const [enhancedResults, treatmentResults] = await Promise.all([
          processEnhancedBlandTasks(enhancedBlandTasks),
          processTreatmentTasks(treatmentTasks),
        ]);

        const totalSuccess = enhancedResults.success + treatmentResults.success;
        const totalFailed = enhancedResults.failed + treatmentResults.failed;
        const totalSkipped =
          initialSkippedCount + (treatmentResults.skipped || 0);

        if (totalSuccess > 0 && totalFailed === 0) {
          let message = `Started ${totalSuccess} Voice AI call${totalSuccess > 1 ? 's' : ''}`;
          if (totalSkipped > 0) {
            message += ` (${totalSkipped} skipped)`;
          }
          toast.success(message);
        } else if (totalSuccess > 0 && totalFailed > 0) {
          toast.warning(
            `Started ${totalSuccess} Voice AI call${totalSuccess > 1 ? 's' : ''}, ${totalFailed} failed`,
          );
        } else {
          toast.error('Failed to start Voice AI calls');
        }
      } catch {
        toast.error('Failed to start Voice AI calls');
      } finally {
        setIsLoading(false);
      }
    },
    [processEnhancedBlandTasks, processTreatmentTasks],
  );

  return {
    triggerVoiceAI,
    isLoading,
  };
};
